import Decimal from './break_infinity';
import {p5, ri} from './runner-idle';
import {Graphics as p5Graphics, Color as p5Color, Image as p5Image} from 'p5';
import {Vector} from './Vector';
import {
  BehaviorSubject,
  ConnectableObservable,
  bufferCount,
  filter,
  first,
  from,
  map,
  of,
  pairwise,
  switchMap,
  tap,
} from 'rxjs';
import {Panel} from './panel';
import {MapPanel} from './map';
import {Region, createLine} from './Region';
import {over} from 'ramda';

export const setupP5 = (mapPanel: MapPanel) => (p: typeof p5) => {
  let zoomScale = new Decimal(1);
  let gridSize = 10;
  let playerX = 0;
  let playerY = 0;
  let playerImage: p5Image;
  let pCanvas;
  let overlay: p5Graphics;

  function drawGrid(left: number, top: number, width: number, height: number) {
    const oldGridSize = gridSize;
    // The zoomScale is 1/100 the distance to the edge of the map
    // The grid size is at least 1/10 of the distance to the edge of the map,
    // rounded up to the nearest power of 10.
    // e.g. 20x20 map = zoomScale of 0.1 and gridSize of 1m
    gridSize = Math.pow(10, Math.ceil(zoomScale.times(100).div(10).log10()));
    if (gridSize != oldGridSize) {
      ri.gridSize = gridSize;
    }
    left = (Math.floor(left / gridSize) * gridSize) / zoomScale.toNumber();
    top = (Math.ceil(top / gridSize) * gridSize) / zoomScale.toNumber();
    width = (Math.ceil(width / gridSize + 1) * gridSize) / zoomScale.toNumber();
    height =
      (Math.ceil(height / gridSize + 1) * gridSize) / zoomScale.toNumber();

    let tempZoomScale = new Decimal(zoomScale);

    while (tempZoomScale.gt(1)) {
      tempZoomScale = tempZoomScale.div(10);
    }
    const right = left + width;
    const bottom = top - height;
    const adjustedGridSize = gridSize / zoomScale.toNumber();

    p.push();
    p.blendMode(p.DARKEST);
    p.stroke(0, 0, 0, 40);
    const gridStrokeWeight = Number(ri.settings.map.gridStrength.value);
    p.strokeWeight(gridStrokeWeight);
    for (let x = left; x < right + adjustedGridSize; x += adjustedGridSize) {
      p.line(x, top, x, bottom);
    }
    for (
      let y = top;
      y > bottom - adjustedGridSize;
      y -= gridSize / zoomScale.toNumber()
    ) {
      p.line(left, y, right, y);
    }
    p.pop();
  }
  p.preload = function () {
    const imageStrings = ['player', 'slime'];
    imageStrings
      .map((imageString) => `./images/${imageString}.png`)
      .forEach((path) => p.loadImage(path));
  };
  p.setup = function () {
    playerImage = p.loadImage('./images/player.png');
    pCanvas = p.createCanvas(...mapPanel.initialSize);
    overlay = p.createGraphics(...mapPanel.initialSize);
    ri.overlay = overlay;
    pCanvas.style('visibility', 'visible');
    pCanvas.mousePressed(function (event: MouseEvent) {
      p.push();
      let clickX = new Decimal(p.mouseX)
        .plus(playerX)
        .minus(p.width / 2)
        .times(zoomScale);
      let clickY = new Decimal(p.height - p.mouseY)
        .plus(playerY)
        .minus(p.height / 2)
        .times(zoomScale);
      if (ri.settings.map.forceTargetOntoGrid) {
        clickX = clickX.round();
        clickY = clickY.round();
      }
      const clickVector = new Vector(clickX, clickY);
      if (event.shiftKey) {
        ri.targetPositions$.next(
          ri.targetPositions$.value.concat([clickVector])
        );
      } else {
        ri.targetPosition = clickVector;
      }
      const clickXEl = mapPanel.shadowRoot?.getElementById(
        'targetX-input'
      ) as HTMLInputElement;
      if (clickXEl) clickXEl.value = String(clickX.toNumber());
      const clickYEl = ri.shadowRoot?.getElementById(
        'targetY-input'
      ) as HTMLInputElement;
      if (clickYEl) clickYEl.value = String(clickY.toNumber());
      p.pop();
      ri.requestUpdate();
    });
    p.noSmooth();
    p.frameRate(30);
    p.rectMode(p.CENTER);
  };

  let builtRegions = false;
  const regions: Set<Region> = new Set();
  p.draw = function () {
    p.clear(0, 0, 0, 0);
    zoomScale = new Decimal(10).pow(
      new Decimal(
        (
          mapPanel.shadowRoot?.getElementById('zoom-range') as HTMLInputElement
        ).value
      ).negate()
    );
    playerX = ri.drawPosition.x.div(zoomScale).toNumber();
    playerY = ri.drawPosition.y.div(zoomScale).toNumber();
    const targetX: number = ri.targetPosition[0].div(zoomScale).toNumber();
    const targetY: number = ri.targetPosition[1].div(zoomScale).toNumber();
    // Center the grid and make up positive y
    p.translate(p.width / 2, p.height / 2);
    p.scale(1, -1);
    // Center the canvas on the player
    p.translate(-playerX, -playerY);
    // Set other general stuff up
    p.blendMode(p.BLEND);
    // p.gridOutput();
    // white on black by default
    const backgroundColor: p5Color = p.color(100, 100, 100); // grey
    const outsideVisibleColor: p5Color = p.color(0, 0, 0, 0); // grass green
    // let backgroundColor: number[] = [20, 42, 76]; // deep blue
    const fillColor: p5Color = p.color(255);
    const strokeColor: p5Color = p.color(255);

    // if (ri.settings.map.invertColors) {
    //   backgroundColor = backgroundColor.map((v) => 255 - v);
    //   fillColor = fillColor.map((v) => 255 - v);
    //   strokeColor = strokeColor.map((v) => 255 - v);
    // }
    p.background(outsideVisibleColor);
    p.fill(fillColor);
    p.stroke(strokeColor);
    // Clip the scene outside the player's view range
    // p.push();
    // p.fill(127);
    // p.circle(playerX, playerY, (2 * ri.visionRange) / zoomScale.toNumber());
    // p.pop();

    p.push();
    // p.scale(zoomScale.reciprocal().toNumber());
    // p.image(backgroundImage, 0, 0);
    p.pop();

    // Draw extra regions
    function buildRegionAtOrigin(name: string, radius: Decimal) {
      buildRegion({
        name,
        topLeft: new Vector(radius.negate(), radius.negate()),
        width: radius.times(2),
        height: radius.times(2),
        fillColor: [0, 0, 0, 0],
        z: new Decimal(10),
      });
    }
    function buildRegion(overrides: Partial<Region>) {
      regions.add(new Region(overrides));
    }
    if (!builtRegions) {
      builtRegions = true;
      // buildRegionAtOrigin('Meter', new Decimal(1));
      // buildRegionAtOrigin('Kilometer', new Decimal(1e3));
      // buildRegionAtOrigin('Mile', new Decimal(1609.344));
      // buildRegionAtOrigin('Earth (radius)', new Decimal(6.371e6));
      // buildRegionAtOrigin('Earth (diameter)', new Decimal(12.756e6));
      // buildRegionAtOrigin('Moon from Earth', new Decimal(384.4e6));
      // buildRegionAtOrigin('Sun from Earth (1 AU)', new Decimal('150e9'));
      // buildRegionAtOrigin('Solar System (radius)', new Decimal('14.1e12'));
      // buildRegionAtOrigin('Light-day', new Decimal('25.9e12'));
      // buildRegionAtOrigin('Light-year', new Decimal('9.5e15'));
      // buildRegionAtOrigin('Proxima Centauri', new Decimal('39.9e15'));
    }
    regions.forEach((region) => region.draw(p, zoomScale));

    // Draw landmarks
    Object.values(ri.landmarks)
      .filter((l) => {
        if (l.shape.distance(ri.position).gt(ri.visionRange)) return false;
        if (l.temporaryFinished$.value) {
          return false;
        }
        if (l.visible$ instanceof BehaviorSubject) {
          return l.visible$.value;
        }
        return true;
      })
      .sort((a, b) => {
        return a.z.minus(b.z).toNumber();
      })
      .forEach((landmark) => {
        landmark.draw(p, zoomScale);
      });
    // Draw target position
    p.push();
    p.fill(255, 191, 0);
    p.stroke(255, 0, 0);
    p.textSize(16);
    p.text('☩', targetX - 7, targetY + 6);
    ri.targetPositions$
      .pipe(
        first(),
        filter((targetPositions) => targetPositions.length >= 2),
        tap((v) => console.log(`1 ${v}`)),
        switchMap(from),
        tap((v) => console.log(`2 ${v}`)),
        pairwise(),
        tap((v) => console.log(`3 ${v}`))
      )
      .subscribe(([a, b]) =>
        p.line(
          a.x.div(zoomScale).toNumber(),
          a.y.div(zoomScale).toNumber(),
          b.x.div(zoomScale).toNumber(),
          b.y.div(zoomScale).toNumber()
        )
      );
    p.pop();

    // Draw actors
    ri.actors
      .filter((actor) => {
        const distance = actor.position.distance(ri.position);
        return (
          distance.lt(actor.interactionRange.plus(ri.visionRange)) ||
          distance.lt(actor.bodySize.plus(ri.visionRange))
        );
      })
      .forEach((actor) => actor.draw(p, zoomScale));
    // Draw player
    const minPlayerPixels = 5;
    p.push();
    const adjustedPlayerWidth = new Decimal(playerImage.width)
      .min(1)
      .div(zoomScale)
      .max(minPlayerPixels)
      .toNumber();
    const adjustedPlayerHeight = new Decimal(playerImage.height)
      .min(1)
      .div(zoomScale)
      .max(minPlayerPixels)
      .toNumber();
    p.translate(
      playerX - adjustedPlayerWidth / 2,
      playerY + adjustedPlayerHeight / 2
    );
    p.scale(1, -1);
    p.image(playerImage, 0, 0, adjustedPlayerWidth, adjustedPlayerHeight);
    p.pop();
    drawGrid(
      (playerX - p.width / 2) * zoomScale.toNumber(),
      (playerY + p.height / 2) * zoomScale.toNumber(),
      p.width * zoomScale.toNumber(),
      p.height * zoomScale.toNumber()
    );
    // Draw overlay - Mask + UI features
    overlay.push();
    overlay.fill(0);
    overlay.rect(0, 0, p.width, p.height);
    overlay.erase();
    overlay.circle(
      p.width / 2,
      p.height / 2,
      ri.visionRange.times(2).div(zoomScale).toNumber()
    );
    overlay.noErase();
    overlay.pop();
    if (ri.settings.map.showPlayerTrail) {
      p.push();
      p.stroke(0);
      let lastX: Decimal | undefined, lastY: Decimal | undefined;
      ri.lastPositions.forEach((position) => {
        if (lastX && lastY) {
          p.line(
            lastX.div(zoomScale).toNumber(),
            lastY.div(zoomScale).toNumber(),
            position.x.div(zoomScale).toNumber(),
            position.y.div(zoomScale).toNumber()
          );
        }
        lastX = position.x;
        lastY = position.y;
      });
      if (lastX && lastY) {
        p.line(
          lastX.div(zoomScale).toNumber(),
          lastY.div(zoomScale).toNumber(),
          playerX,
          playerY
        );
      }
      p.pop();
    }
    // Draw a line indicating where the player will move next,
    // based on how it moved last time.
    if (
      ri.settings.map.showPlayerVelocityLine &&
      ri.lastPositions.length >= 2
    ) {
      p.push();
      p.strokeWeight(2);
      p.stroke(240, 240, 240);
      p.line(
        playerX,
        playerY,
        ri.position.x
          .times(2)
          .minus(ri.lastPositions[ri.lastPositions.length - 2].x)
          .div(zoomScale)
          .toNumber(),
        ri.position.y
          .times(2)
          .minus(ri.lastPositions[ri.lastPositions.length - 2].y)
          .div(zoomScale)
          .toNumber()
      );
      p.pop();
    }
    overlay.updatePixels();
    p.image(overlay, playerX - p.width / 2, playerY - p.height / 2);
  };
};
